import { useCallback } from 'react';
import { type NavigateOptions, useLocation, useNavigate } from 'react-router-dom';

/**
 * Drop-in replacement for react-router's `useNavigate()` that preserves the current
 * URL's search params (e.g. `?view=kanban`) across navigation, unless the target path
 * already specifies its own query string.
 *
 * Returning `navigate` directly (rather than a `withSearch(path)` helper you have to
 * remember to wrap calls in) removes the foot-gun: there's only one `navigate` in scope.
 */
export function useSearchPreservingNavigate() {
  const routerNavigate = useNavigate();
  const location = useLocation();

  const navigate = useCallback(
    (path: string, options?: NavigateOptions) => {
      const target = path.includes('?') ? path : `${path}${location.search}`;
      routerNavigate(target, options);
    },
    [routerNavigate, location.search]
  );

  return navigate;
}
